import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import { useCart } from "../context/CartContext";
import styles from "./CheckoutPage.module.css";
import axios from "axios";

// AUTOMATIC SWITCH: Uses localhost in development, real domain in production
const API_BASE_URL =
  window.location.hostname === "localhost"
    ? "/api"
    : "https://dukamo.dukamocoffee.com/api";

function CheckoutPage() {
  const { cartItems, deliveryOption, clearCart } = useCart();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState("+251");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    notes: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    // If user hasn't picked a delivery method, send them back to choose one
    if (!deliveryOption && cartItems.length > 0) {
      navigate("/delivery");
    }

    if (deliveryOption?.addressData) {
      const addr = deliveryOption.addressData;
      const methodId = deliveryOption.id;

      let autoCity = "Addis Ababa";
      let autoCode = "+251";

      if (methodId === "delivery_hawassa") {
        autoCity = "Hawassa";
      } else if (methodId === "delivery_us") {
        autoCity = "United States";
        autoCode = "+1";
      }

      setFormData((prev) => ({
        ...prev,
        address: addr.addressName || "",
        city: autoCity,
      }));

      setCountryCode(autoCode);
    }
  }, [deliveryOption, cartItems, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const onlyNums = value.replace(/\D/g, "");
      let limit = 15;
      if (countryCode === "+251") limit = 9;
      else if (countryCode === "+1") limit = 10;
      else if (countryCode === "+44") limit = 11;

      if (onlyNums.length > limit) return;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const subtotal = cartItems.reduce((total, item) => {
    const priceString = String(item.price).replace(/Br|,/g, "").trim();
    const price = parseFloat(priceString);
    if (!isNaN(price)) {
      return total + price * item.quantity;
    }
    return total;
  }, 0);

  const shippingCost = deliveryOption ? deliveryOption.price : 0;
  const finalTotal = subtotal + shippingCost;

  // --- 💎 UPDATED SUBMIT HANDLER (MAINTAINING ALL LOGIC) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 1. Clean cart items and map IDs
      const cleanedOrderItems = cartItems.map((item) => {
        const numericPrice = parseFloat(
          String(item.price).replace(/Br|,/g, "").trim()
        );

        return {
          name: item.name,
          quantity: item.quantity,
          image: item.image,
          price: numericPrice,
          product: item._id || item.id,
        };
      });

      // 2. Build the Payload
      const orderPayload = {
        customer: {
          ...formData,
          phone: `${countryCode}${formData.phone}`,
          deliveryMethod: deliveryOption?.title || "Standard Delivery",
        },
        orderItems: cleanedOrderItems,
        shippingAddress: formData.address,
        city: formData.city,
        itemsPrice: subtotal, // Added for backend verification
        shippingPrice: shippingCost,
        totalPrice: finalTotal,
      };

      // 3. Create Order in Database
      const { data: newOrder } = await axios.post(
        `${API_BASE_URL}/orders`,
        orderPayload
      );

      /**
       * 4. 🔒 SECURE INITIATION
       * We pass both orderId and deliveryMethodId (e.g., 'delivery_addis')
       * to the backend to force the correct 300/200/2350 prices.
       */
      const { data: paymentData } = await axios.post(
        `${API_BASE_URL}/payment/initiate`,
        {
          orderId: newOrder._id,
          deliveryMethodId: deliveryOption.id, // SENDS 'delivery_addis', etc.
        }
      );

      // 5. Redirect to Gateway
      if (paymentData.url) {
        window.location.href = paymentData.url;
      } else {
        throw new Error("Payment URL not provided by gateway.");
      }
    } catch (err) {
      console.error("Checkout Error:", err);
      alert(
        err.response?.data?.details?.Reason ||
          err.response?.data?.error ||
          "Order failed."
      );
    } finally {
      setLoading(false);
    }
  };

  const isCartEmpty = cartItems.length === 0;

  return (
    <div className={styles.checkoutPage}>
      <Navbar />
      <div className={styles.cartHero}>
        <h1>Checkout</h1>
      </div>
      <div className={styles.checkoutContainer}>
        {isCartEmpty ? (
          <div className={styles.cartEmpty}>
            <p>Your cart is empty.</p>
            <Link to="/products" className={styles.cartContinueShopping}>
              Start Shopping
            </Link>
          </div>
        ) : (
          <form className={styles.checkoutForm} onSubmit={handleSubmit}>
            <div className={styles.billingDetails}>
              <h2>Billing Details</h2>

              <div className={styles.formRowGrid}>
                <div className={styles.formGroup}>
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="phone">Phone Number *</label>
                <div className={styles.phoneInputGroup}>
                  <select
                    className={styles.phoneSelect}
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                  >
                    <option value="+251">🇪🇹 +251</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                  </select>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="address">Address *</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="city">Town / City *</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="notes">Order Notes (optional)</label>
                <textarea
                  id="notes"
                  name="notes"
                  rows="4"
                  value={formData.notes}
                  onChange={handleInputChange}
                ></textarea>
              </div>
            </div>

            <div className={styles.orderSummary}>
              <h2>Your Order</h2>
              <div className={styles.summaryBox}>
                {cartItems.map((item) => (
                  <div className={styles.summaryRow} key={item.id}>
                    <span>
                      {item.name} &times; {item.quantity}
                    </span>
                    <span>
                      {(
                        parseFloat(
                          String(item.price).replace(/Br|,/g, "").trim()
                        ) * item.quantity
                      ).toLocaleString()}{" "}
                      Br
                    </span>
                  </div>
                ))}

                <div
                  className={styles.summaryRow}
                  style={{ borderTop: "1px solid #333", paddingTop: "15px" }}
                >
                  <span>Subtotal</span>
                  <span>{subtotal.toLocaleString()} Br</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>Shipping ({deliveryOption?.title})</span>
                  <span>
                    {shippingCost > 0
                      ? `+${shippingCost.toLocaleString()} Br`
                      : "Free"}
                  </span>
                </div>
                <div className={`${styles.summaryRow} ${styles.total}`}>
                  <span>Total</span>
                  <span>{finalTotal.toLocaleString()} Br</span>
                </div>
              </div>

              <button
                type="submit"
                className={styles.checkoutBtn}
                disabled={loading}
              >
                {loading ? "Redirecting..." : "Pay"}
              </button>
            </div>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default CheckoutPage;
